// Web Crypto API를 사용한 RSA-OAEP 암호화
export interface PublicKeyResponse {
  code: number;
  status: string;
  message: string;
  data: string; // Base64 인코딩된 공개키
}

// RSA-OAEP 암호화 클래스
export class RSAOAEPEncryption {
  private publicKey: CryptoKey | null = null;

  // Base64 공개키를 CryptoKey로 변환
  async setPublicKey(base64PublicKey: string): Promise<void> {
    try {
      // PEM 헤더/푸터 제거 및 Base64 디코딩
      const pemKey = this.formatPublicKey(base64PublicKey);
      const base64Key = pemKey
        .replace(/-----BEGIN PUBLIC KEY-----/, '')
        .replace(/-----END PUBLIC KEY-----/, '')
        .replace(/\s/g, '');
      
      // Base64를 ArrayBuffer로 변환
      const binaryDer = this.base64ToArrayBuffer(base64Key);
      
      // CryptoKey 생성
      this.publicKey = await window.crypto.subtle.importKey(
        'spki',
        binaryDer,
        {
          name: 'RSA-OAEP',
          hash: { name: 'SHA-256' }
        },
        false,
        ['encrypt']
      );
      
      /// console.log('RSA-OAEP 공개키 설정 완료');
    } catch (error) {
      /// console.error('공개키 설정 실패:', error);
      throw new Error('공개키 설정에 실패했습니다: ' + error);
    }
  }

  // RSA-OAEP로 데이터 암호화
  async encryptData(data: string): Promise<string> {
    if (!this.publicKey) {
      throw new Error('공개키가 설정되지 않았습니다.');
    }

    try {
      // 문자열을 ArrayBuffer로 변환
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);
      
      // RSA-OAEP with SHA-256으로 암호화
      const encryptedBuffer = await window.crypto.subtle.encrypt(
        {
          name: 'RSA-OAEP'
        },
        this.publicKey,
        dataBuffer
      );
      
      // ArrayBuffer를 Base64로 변환
      const encryptedBase64 = this.arrayBufferToBase64(encryptedBuffer);
      /// console.log('RSA-OAEP 암호화 성공');
      return encryptedBase64;
    } catch (error) {
      /// console.error('암호화 실패:', error);
      throw new Error('데이터 암호화에 실패했습니다: ' + error);
    }
  }

  // Base64 공개키를 PEM 형식으로 변환
  private formatPublicKey(base64Key: string): string {
    // 이미 PEM 형식인지 확인
    if (base64Key.includes('-----BEGIN PUBLIC KEY-----')) {
      return base64Key;
    }

    // Base64 문자열을 64문자씩 줄바꿈
    const formattedKey = base64Key.match(/.{1,64}/g)?.join('\n') || base64Key;
    
    return `-----BEGIN PUBLIC KEY-----\n${formattedKey}\n-----END PUBLIC KEY-----`;
  }

  // Base64를 ArrayBuffer로 변환
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = window.atob(base64);
    const length = binaryString.length;
    const bytes = new Uint8Array(length);
    
    for (let i = 0; i < length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    return bytes.buffer;
  }

  // ArrayBuffer를 Base64로 변환
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    
    return window.btoa(binary);
  }
}

// 전역 RSA-OAEP 인스턴스
export const rsaOAEPEncryption = new RSAOAEPEncryption();